/**
 * Email Authentication Modal
 * 
 * 이메일/비밀번호 회원가입 및 로그인 플로우
 * - 이메일 입력 후 비밀번호 필드가 동적으로 나타남
 */

import { useState, useEffect, useRef } from 'react';
import { useAuth, type TermsAgreement } from '../auth';
import { useLanguage } from '../i18n';
import TermsModal from './TermsModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

// 비밀번호 정책 검사
function checkPasswordPolicy(password: string) {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>\-_=+\[\]\\;'`~]/.test(password),
  };
}

function isPasswordValid(password: string): boolean {
  const checks = checkPasswordPolicy(password);
  return Object.values(checks).every(Boolean);
}

export default function EmailAuthModal({ isOpen, onClose, initialMode = 'login' }: Props) {
  const { t } = useLanguage();
  const {
    checkEmail,
    startEmailRegister,
    completeEmailRegister,
    resendOTP,
    loginWithEmail,
    error,
    clearError,
    pendingEmailRegister,
    setPendingEmailRegister,
  } = useAuth();

  // 단계: email-password (통합) → otp → terms
  const [step, setStep] = useState<'email-password' | 'otp' | 'terms'>('email-password');
  const [mode, setMode] = useState<'login' | 'register' | null>(null); // null = 아직 이메일 확인 안 됨
  
  // 입력값
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  
  // 상태
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailChecked, setIsEmailChecked] = useState(false); // 이메일 확인 완료 여부
  const [localError, setLocalError] = useState<string | null>(null);
  const [, setExistingProviders] = useState<{ google: boolean; github: boolean }>({ google: false, github: false });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  
  // 비밀번호 필드 ref (포커스용)
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  
  // 비밀번호 검증 상태
  const passwordChecks = checkPasswordPolicy(password);
  const passwordValid = isPasswordValid(password);
  const passwordsMatch = password === passwordConfirm;

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setStep('email-password');
      setMode(null);
      setIsEmailChecked(false);
      setEmail('');
      setPassword('');
      setPasswordConfirm('');
      setName('');
      setOtp('');
      setLocalError(null);
      clearError();
      
      // 이전에 진행 중이던 이메일 회원가입이 있으면 복원
      if (pendingEmailRegister) {
        setEmail(pendingEmailRegister.email);
        setName(pendingEmailRegister.name || '');
        setStep('otp');
        setMode('register');
        setIsEmailChecked(true);
      }
    }
  }, [isOpen, initialMode, pendingEmailRegister, clearError]);

  // OTP 재발송 쿨다운 타이머
  useEffect(() => {
    if (otpResendCooldown > 0) {
      const timer = setTimeout(() => setOtpResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpResendCooldown]);

  // 이메일 확인
  const handleEmailSubmit = async () => {
    if (!email) {
      setLocalError(t.auth.emailRequired);
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalError(t.auth.invalidEmail);
      return;
    }
    
    setIsLoading(true);
    setLocalError(null);
    
    try {
      const result = await checkEmail(email);
      
      if (result.exists) {
        // 기존 사용자
        setExistingProviders({ google: result.google_connected, github: result.github_connected });
        
        if (result.has_password) {
          // 비밀번호가 있으면 로그인 모드
          setMode('login');
          setIsEmailChecked(true);
          // 비밀번호 필드에 포커스
          setTimeout(() => passwordInputRef.current?.focus(), 100);
        } else {
          // OAuth만 있는 사용자 → 안내 메시지
          const providers = [];
          if (result.google_connected) providers.push('Google');
          if (result.github_connected) providers.push('GitHub');
          setLocalError(t.auth.accountConnected.replace('{provider}', providers.join(', ')));
        }
      } else {
        // 신규 사용자 → 회원가입 모드
        setMode('register');
        setIsEmailChecked(true);
        // 이름 필드에 포커스
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Error checking email');
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 제출 (로그인 또는 회원가입 시작)
  const handlePasswordSubmit = async () => {
    if (!password) {
      setLocalError(t.auth.passwordRequired);
      return;
    }
    
    if (mode === 'register') {
      if (!passwordValid) {
        setLocalError(t.auth.invalidPassword);
        return;
      }
      if (!passwordsMatch) {
        setLocalError(t.auth.passwordMismatch);
        return;
      }
    }
    
    setIsLoading(true);
    setLocalError(null);
    
    try {
      if (mode === 'login') {
        await loginWithEmail(email, password);
        onClose();
      } else {
        // 회원가입 → OTP 발송
        await startEmailRegister(email, password, name || undefined);
        setStep('otp');
        setOtpResendCooldown(60);
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  // 이메일 변경 시 비밀번호 필드 초기화
  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
    if (isEmailChecked) {
      // 이메일이 변경되면 비밀번호 입력 영역 숨김
      setIsEmailChecked(false);
      setMode(null);
      setPassword('');
      setPasswordConfirm('');
      setName('');
      setLocalError(null);
    }
  };

  // OTP 검증 → 약관 동의 모달 열기
  const handleOtpSubmit = async () => {
    if (otp.length !== 6) {
      setLocalError('6자리 인증 코드를 입력해주세요');
      return;
    }
    
    // 약관 동의 모달 열기
    setShowTermsModal(true);
  };

  // 약관 동의 완료 → 회원가입 완료
  const handleTermsAgree = async (terms: TermsAgreement) => {
    setIsLoading(true);
    setLocalError(null);
    
    try {
      await completeEmailRegister(email, otp, terms);
      setShowTermsModal(false);
      onClose();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Verification failed');
      setShowTermsModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  // OTP 재발송
  const handleResendOtp = async () => {
    if (otpResendCooldown > 0) return;
    
    setIsLoading(true);
    setLocalError(null);
    
    try {
      await resendOTP(email);
      setOtpResendCooldown(60);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Resend failed');
    } finally {
      setIsLoading(false);
    }
  };

  // 뒤로가기
  const handleBack = () => {
    if (step === 'otp') {
      setStep('email-password');
      setOtp('');
    }
    setLocalError(null);
  };

  // 이메일 확인 초기화
  const handleResetEmail = () => {
    setIsEmailChecked(false);
    setMode(null);
    setPassword('');
    setPasswordConfirm('');
    setName('');
    setLocalError(null);
  };

  // 모달 닫기
  const handleClose = () => {
    setPendingEmailRegister(null);
    onClose();
  };

  if (!isOpen) return null;

  const displayError = localError || error;

  return (
    <>
      <div 
        className="fixed inset-0 z-[10002] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <div className="bg-terminal-bg border border-terminal-border rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* 헤더 */}
          <div className="px-6 py-4 border-b border-terminal-border bg-terminal-dim/20 flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-terminal-text">
                {step === 'email-password' && (
                  isEmailChecked 
                    ? (mode === 'login' ? t.auth.login : t.auth.signUp)
                    : t.auth.continueWithEmail
                )}
                {step === 'otp' && t.auth.otpTitle}
              </h3>
              {step === 'email-password' && !isEmailChecked && (
                <p className="text-xs text-terminal-muted mt-1">{t.auth.quickLoginDesc}</p>
              )}
            </div>
            {step === 'otp' && (
              <button
                onClick={handleBack}
                className="text-terminal-muted hover:text-terminal-text transition-colors"
              >
                ← 뒤로
              </button>
            )}
          </div>

          <div className="p-6 space-y-4">
            {/* 에러 메시지 */}
            {displayError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {displayError}
              </div>
            )}

            {/* Step 1: 이메일 + 비밀번호 입력 (통합) */}
            {step === 'email-password' && (
              <>
                {/* 이메일 필드 */}
                <div>
                  <label className="block text-sm text-terminal-muted mb-2">{t.auth.email}</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (isEmailChecked) {
                            // 이미 확인된 상태면 비밀번호 필드로 포커스
                            if (mode === 'register') {
                              nameInputRef.current?.focus();
                            } else {
                              passwordInputRef.current?.focus();
                            }
                          } else {
                            handleEmailSubmit();
                          }
                        }
                      }}
                      placeholder={t.auth.emailPlaceholder}
                      className={`w-full px-4 py-3 bg-terminal-dim border rounded-lg text-terminal-text placeholder:text-terminal-muted/50 focus:outline-none transition-colors ${
                        isEmailChecked 
                          ? 'border-status-go/50 pr-10' 
                          : 'border-terminal-border focus:border-status-go/50'
                      }`}
                      autoFocus={!isEmailChecked}
                      disabled={isLoading && !isEmailChecked}
                    />
                    {isEmailChecked && (
                      <button
                        type="button"
                        onClick={handleResetEmail}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-terminal-muted hover:text-terminal-text text-sm"
                        title="이메일 변경"
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                </div>

                {/* 이메일 확인 버튼 - 아직 확인 안 된 경우만 */}
                {!isEmailChecked && (
                  <button
                    onClick={handleEmailSubmit}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-status-go text-white rounded-lg font-medium hover:bg-status-go/90 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '...' : t.auth.continueWithEmail}
                  </button>
                )}

                {/* 비밀번호 영역 - 이메일 확인 후 동적으로 표시 */}
                {isEmailChecked && mode && (
                  <div className="space-y-4 animate-in slide-in-from-top-2 duration-200">
                    {/* 회원가입 모드: 이름 입력 */}
                    {mode === 'register' && (
                      <div>
                        <label className="block text-sm text-terminal-muted mb-2">{t.auth.name}</label>
                        <input
                          ref={nameInputRef}
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && passwordInputRef.current?.focus()}
                          placeholder={t.auth.namePlaceholder}
                          className="w-full px-4 py-3 bg-terminal-dim border border-terminal-border rounded-lg text-terminal-text placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50"
                        />
                      </div>
                    )}

                    {/* 비밀번호 입력 */}
                    <div>
                      <label className="block text-sm text-terminal-muted mb-2">{t.auth.password}</label>
                      <div className="relative">
                        <input
                          ref={passwordInputRef}
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (mode === 'login') {
                                handlePasswordSubmit();
                              }
                            }
                          }}
                          placeholder={t.auth.passwordPlaceholder}
                          className="w-full px-4 py-3 pr-12 bg-terminal-dim border border-terminal-border rounded-lg text-terminal-text placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50"
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-terminal-muted hover:text-terminal-text"
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>

                    {/* 회원가입 모드: 비밀번호 확인 및 정책 */}
                    {mode === 'register' && (
                      <>
                        <div>
                          <label className="block text-sm text-terminal-muted mb-2">{t.auth.passwordConfirm}</label>
                          <div className="relative">
                            <input
                              type={showPasswordConfirm ? 'text' : 'password'}
                              value={passwordConfirm}
                              onChange={(e) => setPasswordConfirm(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                              placeholder={t.auth.passwordConfirmPlaceholder}
                              className="w-full px-4 py-3 pr-12 bg-terminal-dim border border-terminal-border rounded-lg text-terminal-text placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50"
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-terminal-muted hover:text-terminal-text"
                            >
                              {showPasswordConfirm ? '🙈' : '👁️'}
                            </button>
                          </div>
                        </div>

                        {/* 비밀번호 정책 체크리스트 */}
                        <div className="p-3 bg-terminal-dim/50 rounded-lg text-xs space-y-1">
                          <div className="text-terminal-muted mb-2">{t.auth.passwordPolicy}</div>
                          <div className={passwordChecks.minLength ? 'text-status-go' : 'text-terminal-muted'}>
                            {passwordChecks.minLength ? '✓' : '○'} {t.auth.passwordMinLength}
                          </div>
                          <div className={passwordChecks.hasUppercase ? 'text-status-go' : 'text-terminal-muted'}>
                            {passwordChecks.hasUppercase ? '✓' : '○'} {t.auth.passwordUppercase}
                          </div>
                          <div className={passwordChecks.hasLowercase ? 'text-status-go' : 'text-terminal-muted'}>
                            {passwordChecks.hasLowercase ? '✓' : '○'} {t.auth.passwordLowercase}
                          </div>
                          <div className={passwordChecks.hasNumber ? 'text-status-go' : 'text-terminal-muted'}>
                            {passwordChecks.hasNumber ? '✓' : '○'} {t.auth.passwordNumber}
                          </div>
                          <div className={passwordChecks.hasSpecial ? 'text-status-go' : 'text-terminal-muted'}>
                            {passwordChecks.hasSpecial ? '✓' : '○'} {t.auth.passwordSpecial}
                          </div>
                          {passwordConfirm && (
                            <div className={passwordsMatch ? 'text-status-go' : 'text-status-nogo'}>
                              {passwordsMatch ? '✓' : '✗'} {t.auth.passwordMatch}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* 제출 버튼 */}
                    <button
                      onClick={handlePasswordSubmit}
                      disabled={isLoading || (mode === 'register' && (!passwordValid || !passwordsMatch))}
                      className="w-full px-4 py-3 bg-status-go text-white rounded-lg font-medium hover:bg-status-go/90 transition-colors disabled:opacity-50"
                    >
                      {isLoading ? '...' : (mode === 'login' ? t.auth.login : t.auth.signUp)}
                    </button>

                    {/* 비밀번호 찾기 - 로그인 모드 */}
                    {mode === 'login' && (
                      <div className="text-center">
                        <button
                          onClick={() => {
                            // TODO: 비밀번호 재설정 모달
                            alert('비밀번호 재설정 기능은 준비 중입니다');
                          }}
                          className="text-sm text-terminal-muted hover:text-status-go transition-colors"
                        >
                          {t.auth.forgotPassword}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Step 3: OTP 인증 */}
            {step === 'otp' && (
              <>
                <div className="text-sm text-terminal-muted">
                  {t.auth.otpDescription.replace('{email}', email)}
                </div>
                
                <div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => e.key === 'Enter' && otp.length === 6 && handleOtpSubmit()}
                    placeholder={t.auth.otpPlaceholder}
                    className="w-full px-4 py-4 bg-terminal-dim border border-terminal-border rounded-lg text-center text-2xl font-mono tracking-widest text-terminal-text placeholder:text-terminal-muted/50 focus:outline-none focus:border-status-go/50"
                    autoFocus
                    maxLength={6}
                  />
                </div>

                <div className="text-xs text-terminal-muted text-center">
                  {t.auth.otpExpireInfo.replace('{minutes}', '2')}
                </div>

                <button
                  onClick={handleOtpSubmit}
                  disabled={isLoading || otp.length !== 6}
                  className="w-full px-4 py-3 bg-status-go text-white rounded-lg font-medium hover:bg-status-go/90 transition-colors disabled:opacity-50"
                >
                  {isLoading ? t.auth.otpVerifying : t.auth.otpVerify}
                </button>

                <div className="text-center">
                  <button
                    onClick={handleResendOtp}
                    disabled={isLoading || otpResendCooldown > 0}
                    className="text-sm text-terminal-muted hover:text-status-go transition-colors disabled:opacity-50"
                  >
                    {otpResendCooldown > 0
                      ? t.auth.otpResendCooldown.replace('{seconds}', String(otpResendCooldown))
                      : t.auth.otpResend
                    }
                  </button>
                </div>
              </>
            )}
          </div>

          {/* 푸터 */}
          <div className="px-6 py-4 border-t border-terminal-border bg-terminal-dim/10">
            <button
              onClick={handleClose}
              className="w-full px-4 py-2 text-xs text-terminal-muted hover:text-terminal-text transition-colors"
            >
              {t.terms.cancel}
            </button>
          </div>
        </div>
      </div>

      {/* 약관 동의 모달 */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAgree={handleTermsAgree}
        userInfo={{ name: name || '', email, picture: null }}
        isLoading={isLoading}
      />
    </>
  );
}
